/**
 * Boh Data service
 */
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  chunk,
  find,
  get,
  isEmpty,
  isEqual,
  join,
  set,
  size,
  uniqWith,
} from 'lodash';
import { Observable, observableOf, observableThrowError, rx } from '@shared/rx';
import { ColDef } from 'ag-grid-community';
import { environment } from 'environments/environment';
import { HttpErrorService } from '../http-error/http-error.service';
import { NO_PERMISSION_STATUS } from '@app/shared/constants/http-error';
import {
  EHeaderName,
  IApiListResponse,
  IBohApiListResponse,
  IEmployee,
  IEmployeeSiteStatus,
} from '@app/shared/types';
import {
  batchRequestMap,
  batchRequestWhile,
  defaultBatchMapAccumulatedPredict,
} from '@app/shared/rx-operators';
import { DEFAULT_PAGE_SIZE } from '@config/global-constants';

/**
 * Boh Data service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class BohDataService {
  public static UTILITY_TYPE: string = 'BohDataService';

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];

  private get baseUrl(): string {
    return environment.xviBackendURI;
  }

  public constructor(
    private http: HttpClient,
    public httpErrorService: HttpErrorService,
  ) {}

  /**
   * This API is used to get the site status of an employee across multiple sites.
   *
   * !CAUTION: And this will return site status including whether the employee is active or inactive in each site.
   *
   * @param documentFilter
   * @param options
   * @returns
   */
  public getEmployeeSiteStatus(
    documentFilter: {
      siteIds: string[];
      employeeId?: string;
      itemsPerPage?: number;
    },
    options?: {
      removeDuplicate?: boolean;
    },
  ): Observable<IApiListResponse<IEmployeeSiteStatus>> {
    const { itemsPerPage = 1000 } = documentFilter;

    const headers = new HttpHeaders({
      [EHeaderName.XSiteIds]: documentFilter.siteIds,
    });

    const params = {
      itemsPerPage,
      pageNumber: 0,
    };

    if (!isEmpty(documentFilter.employeeId)) {
      set(params, 'fieldName', 'EmployeeId');
      set(params, 'value', documentFilter.employeeId);
    }

    return batchRequestWhile({
      initParams: params as any,
      requestPredict: (batchParams) =>
        this.http.get<IBohApiListResponse<IEmployeeSiteStatus>>(
          `${this.baseUrl}/employee/get-site-status`,
          {
            params: batchParams as any,
            headers,
          },
        ) as any,
      nextParamsPredict: (response, currentParams) => {
        return { ...currentParams, pageNumber: currentParams.pageNumber + 1 };
      },
    }).pipe(
      rx.map((siteStatus: IApiListResponse<IEmployeeSiteStatus>) => {
        if (!options?.removeDuplicate || isEmpty(siteStatus?.items)) {
          return siteStatus;
        }

        const uniqueData = uniqWith(
          siteStatus?.items,
          (oneStatus, anotherStatus) =>
            isEqual(oneStatus.EmployeeId, anotherStatus.EmployeeId) &&
            isEqual(oneStatus.SiteId, anotherStatus.SiteId),
        );

        return {
          ...siteStatus,
          items: uniqueData,
          total: size(uniqueData),
        };
      }),
      this.catchHttpErrorAndPickErrorList(),
    );
  }

  public getEmployeeByIds(
    employeeIds: string[],
  ): Observable<IApiListResponse<IEmployee>> {
    if (isEmpty(employeeIds)) {
      return observableOf({
        items: [],
        total: 0,
        current_page: 1,
        page_count: 1,
        page_size: 0,
      } as IApiListResponse<IEmployee>);
    }

    return batchRequestMap(
      chunk(employeeIds, DEFAULT_PAGE_SIZE),
      (chunkEmployeeIds) => {
        return this.http.get<IBohApiListResponse<IEmployee>>(
          `${this.baseUrl}/employee/get-by-ids`,
          {
            params: {
              employee_ids: join(chunkEmployeeIds, ','),
            } as any,
          },
        );
      },
    ).pipe(
      rx.reduce(defaultBatchMapAccumulatedPredict, {}),
      this.catchHttpErrorAndPickErrorList(),
    );
  }

  public getEmployeeById(
    employeeId: string,
  ): Observable<IBohApiListResponse<IEmployee>> {
    return this.http
      .get(`${this.baseUrl}/employee/${employeeId}`)
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  private catchHttpErrorAndPickErrorList =
    (): any =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) => {
          const errorData = get(error, 'errors', {});
          const hasNoPermissionError = find(errorData, (errorItem) =>
            isEqual(errorItem.status, NO_PERMISSION_STATUS),
          );
          if (hasNoPermissionError) {
            return this.httpErrorService.catchNoPermissionHttpError(errorData);
          }

          return observableThrowError(errorData);
        }),
      );
}
